import { FC, useCallback, useMemo, useState } from 'react';

import { iFile, iSourceFile } from '../../../core/models/File';
import Scrollbar from '../../../components/Scrollbar/Scrollbar';
import PdfReader from '../../../components/PdfReader/PdfReader';
import SearchSource from '../../__Common/GlobalSearch/AiSearchResults/SearchSource';
import { useMediaQuery, BreakpointsEnum } from '../../../core/hooks/useMediaQuery';
import useGetNewsArticleReferences from '../../../core/apiHooks/useGetNewsArticleReferences';

import './NewsArticleReferences.styles.scss';

interface iNewsArticleReferencesProps {
    articleId: number;
}

const NewsArticleReferences: FC<iNewsArticleReferencesProps> = ({ articleId }) => {
    const [isReadPopupOpen, setIsReadPopupOpen] = useState(false);
    const [file, setFile] = useState<iSourceFile | null>(null);

    const isMaxMd = useMediaQuery(BreakpointsEnum.MD);
    const { data: referencedPublications, isLoaded } = useGetNewsArticleReferences(articleId);

    const handleRead = useCallback((pdfFile: iFile, title: string, initialPage?: number) => {
        setFile({ ...pdfFile, title, initialPage });
        setIsReadPopupOpen(true);
    }, []);

    const onCloseReadPopup = useCallback(() => {
        setFile(null);
        setIsReadPopupOpen(false);
    }, []);

    const renderReferences = useMemo(() => {
        if (isMaxMd) {
            return (
                <div className="NewsArticleReferences__content-inner">
                    {referencedPublications.map((publication) => (
                        <SearchSource key={publication.id} source={publication} onRead={handleRead} />
                    ))}
                </div>
            );
        }

        return (
            <div className="NewsArticleReferences__content">
                <Scrollbar>
                    <div className="NewsArticleReferences__content-inner">
                        {referencedPublications.map((publication) => (
                            <SearchSource key={publication.id} source={publication} onRead={handleRead} />
                        ))}
                    </div>
                </Scrollbar>
            </div>
        );
    }, [isMaxMd, handleRead, referencedPublications]);

    if (!isLoaded || referencedPublications.length === 0) {
        return null;
    }

    return (
        <>
            <div className="NewsArticleReferences__divider" />
            <div className="NewsArticleReferences">
                <div className="NewsArticleReferences__header">
                    <h3 className="NewsArticleReferences__heading">referenced reports</h3>
                </div>
                {renderReferences}
            </div>

            {!!file && !!file.name && (
                <PdfReader
                    isOpened={isReadPopupOpen}
                    close={onCloseReadPopup}
                    title={file.title}
                    fileName={file.name}
                    initialPage={file.initialPage}
                />
            )}
        </>
    );
};

export default NewsArticleReferences;
